import React, { useState, useEffect, useCallback } from 'react';
import { Exam, Question, UserResponse } from '../types';
import { COLORS } from '../constants';
import QuestionTextRenderer from './QuestionTextRenderer';

interface TestInterfaceProps {
  exam: Exam;
  onSubmit: (responses: UserResponse[], timeTaken: number) => void;
  onExit: () => void;
}

const TestInterface: React.FC<TestInterfaceProps> = ({ exam, onSubmit, onExit }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<UserResponse[]>(() =>
    exam.questions.map(q => ({
      questionId: q.id,
      selectedOptionId: null,
      status: 'not_visited',
      timeSpent: 0
    }))
  );
  const [timeLeft, setTimeLeft] = useState(exam.durationMinutes * 60);

  // Auto-Save / Resume states
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedState, setSavedState] = useState<{
    responses: UserResponse[];
    timeLeft: number;
    currentQuestionIndex: number;
  } | null>(null);

  // Check for saved progress on mount
  useEffect(() => {
    const localKey = `thrusted_active_test_${exam.id}`;
    const stored = localStorage.getItem(localKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.responses && typeof parsed.timeLeft === 'number') {
          setSavedState(parsed);
          setShowResumePrompt(true);
        }
      } catch (e) {
        console.error("Failed to parse saved test progress", e);
        localStorage.removeItem(localKey);
      }
    }
  }, [exam.id]);

  // Resume handler
  const handleResume = () => {
    if (savedState) {
      setResponses(savedState.responses);
      setTimeLeft(savedState.timeLeft);
      setCurrentQuestionIndex(savedState.currentQuestionIndex || 0);
    }
    setShowResumePrompt(false);
  };

  // Start fresh handler
  const handleStartFresh = () => {
    const localKey = `thrusted_active_test_${exam.id}`;
    localStorage.removeItem(localKey);
    setShowResumePrompt(false);
  };

  // Timer (paused if resume prompt is active)
  useEffect(() => {
    if (showResumePrompt) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });

      // Increment time spent on the current question
      setResponses((prevResponses) => {
        const newResponses = [...prevResponses];
        const current = newResponses[currentQuestionIndex];
        if (current) {
          newResponses[currentQuestionIndex] = {
            ...current,
            timeSpent: (current.timeSpent || 0) + 1
          };
        }
        return newResponses;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResumePrompt, currentQuestionIndex]);

  // Auto-save progress
  useEffect(() => {
    if (showResumePrompt) return;

    const localKey = `thrusted_active_test_${exam.id}`;
    const stateToSave = {
      responses,
      timeLeft,
      currentQuestionIndex
    };
    localStorage.setItem(localKey, JSON.stringify(stateToSave));
  }, [responses, timeLeft, currentQuestionIndex, exam.id, showResumePrompt]);

  const currentQuestion = exam.questions[currentQuestionIndex];
  const currentResponse = responses[currentQuestionIndex];

  const handleOptionSelect = (optionId: string) => {
    setResponses((prev) => {
      const newResponses = [...prev];
      newResponses[currentQuestionIndex] = {
        ...newResponses[currentQuestionIndex],
        selectedOptionId: optionId,
        status: newResponses[currentQuestionIndex].status === 'marked' ? 'marked' : 'answered'
      };
      return newResponses;
    });
  };

  const handleMarkForReview = () => {
    setResponses((prev) => {
      const newResponses = [...prev];
      const current = newResponses[currentQuestionIndex];
      newResponses[currentQuestionIndex] = {
        ...current,
        status: 'marked'
      };
      return newResponses;
    });
  };

  const handleClearResponse = () => {
    setResponses((prev) => {
      const newResponses = [...prev];
      newResponses[currentQuestionIndex] = {
        ...newResponses[currentQuestionIndex],
        selectedOptionId: null,
        status: 'visited_unanswered'
      };
      return newResponses;
    });
  };

  const handleNavigation = (index: number) => {
    // Update status of current question if leaving it blank
    setResponses((prev) => {
      const newResponses = [...prev];
      const current = newResponses[currentQuestionIndex];
      if (current.status === 'not_visited') {
        newResponses[currentQuestionIndex].status = 'visited_unanswered';
      }
      return newResponses;
    });
    setCurrentQuestionIndex(index);
  };

  // Keyboard navigation and shortcuts
  useEffect(() => {
    if (showResumePrompt) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = e.key.toLowerCase();

      if (key === '1' || key === 'a') {
        const opt = currentQuestion.options[0];
        if (opt) handleOptionSelect(opt.id);
      } else if (key === '2' || key === 'b') {
        const opt = currentQuestion.options[1];
        if (opt) handleOptionSelect(opt.id);
      } else if (key === '3' || key === 'c') {
        const opt = currentQuestion.options[2];
        if (opt) handleOptionSelect(opt.id);
      } else if (key === '4' || key === 'd') {
        const opt = currentQuestion.options[3];
        if (opt) handleOptionSelect(opt.id);
      }

      if (key === 'm') {
        handleMarkForReview();
      }

      if (key === 'c' || key === 'backspace' || key === 'delete') {
        handleClearResponse();
      }

      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNavigation(Math.min(exam.questions.length - 1, currentQuestionIndex + 1));
      } else if (e.key === 'ArrowLeft') {
        handleNavigation(Math.max(0, currentQuestionIndex - 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentQuestionIndex, currentQuestion, responses, exam.questions.length, showResumePrompt]);

  const handleSubmit = useCallback(() => {
    const timeTaken = (exam.durationMinutes * 60) - timeLeft;
    localStorage.removeItem(`thrusted_active_test_${exam.id}`);
    onSubmit(responses, timeTaken);
  }, [exam.durationMinutes, responses, timeLeft, onSubmit, exam.id]);

  const handleExit = () => {
    if (window.confirm("Are you sure you want to exit the test? Your current progress will be lost.")) {
      localStorage.removeItem(`thrusted_active_test_${exam.id}`);
      onExit();
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string, isSelected: boolean) => {
    if (isSelected) return 'ring-2 ring-slate-800 dark:ring-slate-200'; // Current question indicator
    switch (status) {
      case 'answered': return 'bg-green-500 text-white border-green-500';
      case 'marked': return 'bg-purple-500 text-white border-purple-500';
      case 'not_visited': return 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700';
      case 'visited_unanswered': return 'bg-red-500 text-white border-red-500';
      default: return 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-100 dark:bg-slate-900">
      {/* Resume Prompt Modal */}
      {showResumePrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/65 backdrop-blur-md animate-fade-in">
          <div className="bg-white dark:bg-slate-805 rounded-3xl p-8 max-w-md w-full border border-slate-200 dark:border-slate-700 shadow-glass-lg animate-scale-in text-center">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-950/50 rounded-2xl flex items-center justify-center text-orange-500 mx-auto mb-6 text-2xl shadow-inner">
              <i className="fas fa-history"></i>
            </div>
            <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">
              Resume Test?
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
              We found your saved progress for <span className="font-semibold text-slate-805 dark:text-slate-200">{exam.title}</span>. 
              Would you like to resume where you left off or start a new attempt?
            </p>
            
            {savedState && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-4 mb-6 border border-slate-100 dark:border-slate-800 text-left text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Answered questions:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    {savedState.responses.filter(r => r.selectedOptionId !== null).length} / {exam.questions.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Remaining time:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    {formatTime(savedState.timeLeft)}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleResume}
                className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
              >
                Resume Progress
              </button>
              <button
                onClick={handleStartFresh}
                className="w-full py-3.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-semibold transition-all duration-200"
              >
                Discard & Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex justify-between items-center shadow-sm">
        <h2 className="font-bold text-lg text-slate-800 dark:text-white truncate max-w-xl">{exam.title}</h2>
        <div className="flex items-center space-x-4">
          <div className="text-xl font-mono font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-900 px-3 py-1 rounded">
            <i className="far fa-clock mr-2 text-orange-500"></i>
            {formatTime(timeLeft)}
          </div>
          <button
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-medium transition-colors"
          >
            Submit Test
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Question Area */}
        <main className="flex-1 flex flex-col h-full bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 overflow-hidden p-0">
          {/* Question Header */}
          <div className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 flex-shrink-0">
            <div className="max-w-6xl w-full mx-auto flex justify-between items-center px-8 py-4">
              <span className="text-slate-700 dark:text-slate-300 font-bold text-lg">Question {currentQuestionIndex + 1} of {exam.questions.length}</span>
              <div className="space-x-2">
                <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 dark:bg-slate-900 rounded uppercase text-slate-500 dark:text-slate-400">
                  {currentQuestion.difficulty}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 rounded uppercase">
                  + {exam.positiveMarks} / - {exam.negativeMarks}
                </span>
              </div>
            </div>
          </div>

          {/* Scrollable Question Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-6xl w-full mx-auto p-8 space-y-6">
              <div className="text-slate-850 dark:text-slate-100">
                <QuestionTextRenderer text={currentQuestion.text} className="text-xl font-medium leading-relaxed" />
              </div>

              {/* Rich Media / Image Support */}
              {currentQuestion.imageUrl && (
                <div className="my-6">
                  <img
                    src={currentQuestion.imageUrl}
                    alt="Question Diagram"
                    className="max-w-full max-h-96 rounded-lg border border-slate-200 dark:border-slate-700 object-contain mx-auto shadow-sm"
                  />
                </div>
              )}

              <div className="space-y-3.5">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleOptionSelect(option.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all flex items-center group
                      ${currentResponse.selectedOptionId === option.id
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20 ring-1 ring-orange-500'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-850'
                      }`}
                  >
                    <span className={`w-8 h-8 flex items-center justify-center rounded-full border mr-4 font-semibold transition-colors flex-shrink-0
                      ${currentResponse.selectedOptionId === option.id
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 group-hover:border-slate-400 dark:group-hover:border-slate-500'
                      }`}>
                      {option.id}
                    </span>
                    <span className="text-slate-700 dark:text-slate-300 font-medium">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 flex-shrink-0">
            <div className="max-w-6xl w-full mx-auto flex justify-between items-center px-8 py-4">
              <div className="space-x-3">
                <button
                  onClick={handleMarkForReview}
                  className="px-5 py-2.5 text-purple-600 border border-purple-200 dark:border-purple-800/40 hover:bg-purple-50 dark:hover:bg-purple-950/20 rounded-lg font-medium transition-colors"
                >
                  <i className="fas fa-bookmark mr-2"></i> Mark for Review
                </button>
                <button
                  onClick={handleClearResponse}
                  className="px-5 py-2.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 font-medium transition-colors"
                >
                  Clear Response
                </button>
              </div>
              <div className="space-x-3">
                <button
                  onClick={() => handleNavigation(Math.max(0, currentQuestionIndex - 1))}
                  disabled={currentQuestionIndex === 0}
                  className="px-6 py-2.5 bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 disabled:opacity-50 font-medium transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleNavigation(Math.min(exam.questions.length - 1, currentQuestionIndex + 1))}
                  className="px-6 py-2.5 bg-slate-900 dark:bg-slate-700 text-white rounded-lg hover:bg-slate-800 dark:hover:bg-slate-600 font-medium transition-colors"
                >
                  {currentQuestionIndex === exam.questions.length - 1 ? 'Last Question' : 'Save & Next'}
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Sidebar / Palette */}
        <aside className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col shadow-lg z-10 hidden md:flex">
          <div className="p-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <h3 className="font-semibold text-slate-800 dark:text-white">Question Palette</h3>
          </div>
          <div className="flex-1 p-4 overflow-y-auto palette-scroll">
            <div className="grid grid-cols-5 gap-2">
              {responses.map((resp, idx) => (
                <button
                  key={resp.questionId}
                  onClick={() => handleNavigation(idx)}
                  className={`h-10 w-10 rounded border font-medium text-sm transition-all
                    ${getStatusColor(resp.status, idx === currentQuestionIndex)}
                  `}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center"><div className="w-4 h-4 bg-green-500 rounded mr-2"></div> Answered</div>
              <div className="flex items-center"><div className="w-4 h-4 bg-red-500 rounded mr-2"></div> Not Answered</div>
              <div className="flex items-center"><div className="w-4 h-4 bg-purple-500 rounded mr-2"></div> Marked for Review</div>
              <div className="flex items-center"><div className="w-4 h-4 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded mr-2"></div> Not Visited</div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 text-xs uppercase tracking-wider mb-3">Keyboard Shortcuts</h4>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500">
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800">
                  <span>Select:</span>
                  <span className="font-mono bg-white dark:bg-slate-800 border dark:border-slate-700 px-1.5 py-0.5 rounded shadow-sm font-bold text-slate-700 dark:text-slate-300">1-4 / A-D</span>
                </div>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800">
                  <span>Next:</span>
                  <span className="font-mono bg-white dark:bg-slate-800 border dark:border-slate-700 px-1.5 py-0.5 rounded shadow-sm font-bold text-slate-700 dark:text-slate-300">➔ / Enter</span>
                </div>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800">
                  <span>Prev:</span>
                  <span className="font-mono bg-white dark:bg-slate-800 border dark:border-slate-700 px-1.5 py-0.5 rounded shadow-sm font-bold text-slate-700 dark:text-slate-300">L Arrow</span>
                </div>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800">
                  <span>Review:</span>
                  <span className="font-mono bg-white dark:bg-slate-800 border dark:border-slate-700 px-1.5 py-0.5 rounded shadow-sm font-bold text-slate-700 dark:text-slate-300">M</span>
                </div>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-2 rounded border border-slate-100 dark:border-slate-800 col-span-2">
                  <span>Clear Response:</span>
                  <span className="font-mono bg-white dark:bg-slate-800 border dark:border-slate-700 px-1.5 py-0.5 rounded shadow-sm font-bold text-slate-700 dark:text-slate-300">Backspace / C</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <button onClick={handleExit} className="w-full py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 rounded border border-red-100 dark:border-red-900/30">
              Exit Test
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TestInterface;